<script lang="ts">
	import { getLast12MonthsRanges } from '$lib/utils/date';
	import { toArray } from '$lib/utils/filters/helpers';
	import { FilterStore, getDefaultFilters } from '$lib/utils/filters/store';
	import type { FilterType } from '$lib/utils/filters/types';
	import { NEWS_CATEGORIES } from '$lib/utils/news-filters/constants';
  import { stringToSlug } from '@24hbs/utils';
	import StickyFilters from './StickyFilters.svelte';
	import Filter from './Filter.svelte';
	import FilterDropdown from './FilterDropdown.svelte';
	import { Checkbox } from '$lib/components/atoms/Field';
	import { Grid } from '$lib/components/organisms/Layout';
	import AppliedFiltersWrapper from './AppliedFiltersWrapper.svelte';
	import { ChipButton } from '$lib/components/atoms/Button';
	import { LinkAction } from '$lib/components/atoms/Link';
	import { Loading } from '$lib/components/atoms/Loading';

  export let triggerHideStickyFilters: HTMLElement | null = null;
  export let applyFilter: () => void;
  export let isLoading: boolean;

  const filterStoreInstance = FilterStore.getInstance();
  const filters = filterStoreInstance.filterStore;
  const filtersApplied = filterStoreInstance.filtersApplied;
  const isAnyFilterApplied = filterStoreInstance.isAnyFilterApplied;

  const categoryFacets = filterStoreInstance.categoryFacets;
  const monthFacets = filterStoreInstance.monthFacets;

  const MONTHS_YEARS = getLast12MonthsRanges();

  const onFilterChange = (e) => {
    const newFilters = { ...$filters };

    const selectedFilters = e.detail.data;

    if (selectedFilters.category) {
      newFilters.category = toArray(selectedFilters.category);
    } else {
      newFilters.category = [];
    }
    if (selectedFilters.month) {
      newFilters.month = toArray(selectedFilters.month);
    } else {
      newFilters.month = [];
    }

    filters.update((currentFilters) => {
      return {
        ...currentFilters,
        ...newFilters,
      };
    });

    applyFilter();
  };

  const removeFilter = (filter: FilterType, value?: number | string) => {

    filters.update((currentValue) => {
      switch (filter) {
        case 'category':
        case 'month':
          return {
            ...currentValue,
            [filter]: (currentValue[filter] as (number | string)[]).filter((f) => f !== value),
          };
      }
    });

    applyFilter();
  };

  const removeAllFilters = () => {

    filters.set(getDefaultFilters());
    applyFilter();
  };

  const getFilterText = (filterValue: any[] | any, filterOptions: any[]) => {
    if (!Array.isArray(filterValue)) return filterOptions.find((option) => option.value === filterValue)?.text || '';

    if (filterValue.length > 0) {
      return filterValue
        .map((value) => {
          return filterOptions.find((option) => option.value === value)?.text || '';
        })
        .join(',');
    }
    return '';
  };

  $: categoryText = getFilterText($filters.category, NEWS_CATEGORIES);
  $: monthText = getFilterText($filters.month, MONTHS_YEARS);
</script>

<StickyFilters {triggerHideStickyFilters}>
  <Filter on:removeAllFilters={removeAllFilters} on:change={onFilterChange}>    
    <FilterDropdown title="Tematiche" currentValue={categoryText}>
      {#each NEWS_CATEGORIES as category, index (category)}
        <Checkbox
          id="tema-{index + 1}"
          name="category"
          value={category.value}
          checked={$filters.category?.includes(category.value)}
        >
          <div class="flex flex-1 justify-between">
            <div>{category.text}</div>
              {#if $categoryFacets}
                <div class="facet md:mt-1">
                  {$categoryFacets[stringToSlug(category.value)]}
                </div>
              {/if}
          </div>
        </Checkbox>
      {/each}
    </FilterDropdown>
    <FilterDropdown title="Data Pubblicazione" currentValue={monthText}>
      {#each MONTHS_YEARS as monthYear, index}
        <Checkbox
          id="month-{index}"
          name="month"
          value={monthYear.value}
          checked={$filters.month?.includes(monthYear.value)}
        >
          <div class="flex flex-1 justify-between">
            <span>{monthYear.text}</span>
            {#if $monthFacets}
              <span class="facet">
                {$monthFacets[monthYear.value]}
              </span>
            {/if}
          </div>
        </Checkbox>
      {/each}
    </FilterDropdown>
  </Filter>

  {#if $isAnyFilterApplied}
    <Grid noGrid>
      <AppliedFiltersWrapper>
        {#each $filters.category as category}
          <ChipButton
            value={getFilterText(category, NEWS_CATEGORIES)}
            on:removefilter={() => removeFilter('category', category)}
          />
        {/each}
        {#each $filters.month as month (month)}
          <ChipButton value={getFilterText(month, MONTHS_YEARS)} on:removefilter={() => removeFilter('month', month)} />
        {/each}

        <div style="display: flex;" slot="action">
          {#if $isAnyFilterApplied}
            <LinkAction iconPos="end" icon="reset" on:click={() => removeAllFilters()}>Rimuovi filtri</LinkAction>
          {/if}
        </div>
      </AppliedFiltersWrapper>
    </Grid>
  {/if}
</StickyFilters>

<Loading loading={isLoading} />